import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  ConfirmedMatchExternalRef,
  MatchExternalRefEvent,
  useConfirmedMatchExternalRefs,
  useMatchExternalRefEvents,
  useMatchExternalRefPeek
} from '../../api/useMatchExternalRefs';

/**
 * The signal types the Trade360 corpus actually extracts today. Deliberately
 * NOT the old ENGAGE tab's category set (goal/penalty/corner/VAR/final) —
 * corners and VAR are not extracted here, and fabricating filter chips for
 * data that doesn't exist would be worse than omitting them.
 */
const SIGNAL_TYPES = [
  'pressure',
  'score_move',
  'score_revert',
  'penalty_awarded',
  'penalty_missed'
] as const;
type Trade360SignalType = (typeof SIGNAL_TYPES)[number];

interface SignalTypeDisplay {
  emoji: string;
  color: string;
  label: string;
}

const SIGNAL_TYPE_DISPLAY: Record<Trade360SignalType, SignalTypeDisplay> = {
  pressure: { emoji: '🔥', color: '#ff9800', label: 'pressure' },
  score_move: { emoji: '⚽', color: '#4caf50', label: 'score move' },
  score_revert: { emoji: '↩️', color: '#f44336', label: 'score revert' },
  penalty_awarded: { emoji: '🎯', color: '#e91e63', label: 'penalty awarded' },
  penalty_missed: { emoji: '🚫', color: '#9e9e9e', label: 'penalty missed' }
};

const FALLBACK_SIGNAL_DISPLAY: SignalTypeDisplay = {
  emoji: '📌',
  color: '#9e9e9e',
  label: ''
};

/** Falls back to a neutral pin (with the raw type as the label) for a signal
 * type the backend adds before this map is updated — never hides the row. */
function signalDisplay(signalType: string): SignalTypeDisplay {
  const known = (SIGNAL_TYPE_DISPLAY as Record<string, SignalTypeDisplay>)[
    signalType
  ];
  return known ?? { ...FALLBACK_SIGNAL_DISPLAY, label: signalType };
}

function formatTime(ms: number): string {
  try {
    return new Date(ms).toLocaleTimeString();
  } catch {
    return String(ms);
  }
}

function formatIso(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function MatchIdentityLine({
  home,
  away,
  league
}: {
  home: string;
  away: string;
  league?: string | null;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 600 }}>
        {home} vs {away}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {league ?? '—'}
      </Typography>
    </Stack>
  );
}

/** One discrete event in a bound match's live feed. */
const SignalEventRow = memo(function SignalEventRow({
  event
}: {
  event: MatchExternalRefEvent;
}) {
  const display = signalDisplay(event.signalType);
  return (
    <Paper
      variant="outlined"
      data-testid="trade360-event-row"
      sx={{
        py: 0.5,
        pl: 1.5,
        pr: 1.5,
        mb: 0.5,
        borderLeft: 3,
        borderLeftColor: display.color
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ minHeight: 26, flexWrap: 'wrap' }}
      >
        <Box
          aria-hidden
          sx={{ fontSize: '1rem', lineHeight: 1, minWidth: 22, textAlign: 'center' }}
        >
          {display.emoji}
        </Box>
        <Chip
          size="small"
          label={display.label}
          variant="outlined"
          sx={{
            borderColor: display.color,
            color: display.color,
            height: 20,
            fontSize: '0.7rem',
            '& .MuiChip-label': { px: 0.75 }
          }}
        />
        <Typography variant="body2" sx={{ color: 'common.white' }}>
          {event.subject ?? '—'}
        </Typography>
        {event.magnitude != null && (
          <Chip
            size="small"
            variant="outlined"
            label={`mag ${event.magnitude}`}
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatTime(event.observedAtMs)}
        </Typography>
      </Stack>
    </Paper>
  );
});

/**
 * One auto-surfaced bound match — sourced from `confirmed` (polled), so a
 * match bound elsewhere shows up here without the operator re-adding it.
 * Gets the full discrete event feed via `events`, filtered to the selected
 * signal types. Polling gates on `expanded`, same throttle discipline as the
 * fixture-picker's old embedded panel.
 */
const BoundMatchCard = memo(function BoundMatchCard({
  matchRef,
  selectedTypes,
  registerCard,
  highlighted
}: {
  matchRef: ConfirmedMatchExternalRef;
  selectedTypes: Trade360SignalType[];
  registerCard: (externalId: string, el: HTMLDivElement | null) => void;
  highlighted: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const { data, isLoading, isError } = useMatchExternalRefEvents(matchRef.matchKey, {
    enabled: expanded
  });
  const filtered = useMemo(
    () => (data ?? []).filter(e => selectedTypes.includes(e.signalType as Trade360SignalType)),
    [data, selectedTypes]
  );
  const home = matchRef.competitorByPosition?.['1'] ?? '—';
  const away = matchRef.competitorByPosition?.['2'] ?? '—';

  return (
    <Paper
      variant="outlined"
      ref={el => registerCard(matchRef.externalId, el)}
      data-testid="trade360-bound-card"
      sx={{
        mb: 1.5,
        overflow: 'hidden',
        borderColor: highlighted ? 'primary.main' : 'divider',
        borderWidth: highlighted ? 2 : 1,
        transition: 'border-color 0.3s, border-width 0.3s'
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ p: 1.5, flexWrap: 'wrap', bgcolor: 'background.default' }}
        useFlexGap
      >
        <IconButton size="small" onClick={() => setExpanded(e => !e)} aria-label="toggle match">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <MatchIdentityLine home={home} away={away} />
        <Chip
          size="small"
          variant="outlined"
          label={`fixture ${matchRef.externalId}`}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        <Chip
          size="small"
          color={matchRef.actingEnabled ? 'success' : 'default'}
          label={matchRef.actingEnabled ? 'acting' : 'not acting'}
          sx={{ height: 20 }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          bound {formatIso(matchRef.boundAt)}
        </Typography>
      </Stack>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5, pt: 1 }}>
          {isError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Could not load Trade360 events.
            </Alert>
          )}
          {isLoading && data === undefined && (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
            </Stack>
          )}
          {!isLoading && filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No events yet.
            </Typography>
          )}
          {filtered.map((ev, i) => (
            <SignalEventRow key={`${ev.observedAtMs}-${i}`} event={ev} />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
});

/**
 * One manually-watched fixture the operator typed in that has NO group
 * binding yet — a coarse periodic snapshot via `peek` (Trade360's own API,
 * not our discrete corpus stream), never a discrete event feed. Removing it
 * only drops it from this tab's local state; it never touches the backend.
 */
const UnboundMatchCard = memo(function UnboundMatchCard({
  fixtureId,
  onRemove
}: {
  fixtureId: string;
  onRemove: (fixtureId: string) => void;
}) {
  const { data, isLoading, isError } = useMatchExternalRefPeek(fixtureId, {
    enabled: true
  });
  const home = data?.participants.find(p => p.position === '1')?.name ?? '—';
  const away = data?.participants.find(p => p.position === '2')?.name ?? '—';

  return (
    <Paper variant="outlined" data-testid="trade360-unbound-card" sx={{ mb: 1.5, p: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <MatchIdentityLine home={home} away={away} />
        <Chip
          size="small"
          variant="outlined"
          label={`fixture ${fixtureId}`}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        <Tooltip title="No group binding yet — a periodic snapshot from Trade360's own API, not a live event stream">
          <Chip
            size="small"
            label="unbound — peek only"
            sx={{ height: 20, fontWeight: 700, bgcolor: 'warning.main', color: '#000' }}
          />
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Stop watching">
          <IconButton
            size="small"
            onClick={() => onRemove(fixtureId)}
            aria-label={`stop watching fixture ${fixtureId}`}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {isError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Could not load a Trade360 snapshot for this fixture.
        </Alert>
      )}
      {isLoading && data === undefined && (
        <Stack alignItems="center" sx={{ py: 1 }}>
          <CircularProgress size={18} />
        </Stack>
      )}
      {data && (
        <Stack direction="row" spacing={3} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Scoreboard
            </Typography>
            {data.scoreboard == null || data.scoreboard.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            ) : (
              data.scoreboard.map(s => (
                <Typography key={s.position} variant="body2" sx={{ color: 'common.white' }}>
                  {s.position === '1' ? home : away}: {s.value}
                </Typography>
              ))
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Statistics
            </Typography>
            {data.statistics == null || data.statistics.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            ) : (
              data.statistics.map((s, i) => (
                <Typography
                  key={`${s.statisticType}-${s.position}-${i}`}
                  variant="body2"
                  sx={{ color: 'common.white' }}
                >
                  {s.statisticType} · {s.position === '1' ? home : away}: {s.value}
                </Typography>
              ))
            )}
          </Box>
        </Stack>
      )}
    </Paper>
  );
});

/**
 * The Trade360 live-events tab: matches ever bound via a group's Trade360
 * fixture-binding flow auto-surface here (polled `confirmed`), each with its
 * own discrete event feed (`events`). An operator can also watch an UNBOUND
 * fixture by ID — a purely local, unpersisted list polling the coarser
 * `peek` snapshot instead. Deliberately does NOT have a min-confidence
 * slider or an Auto/Manual/All operations toggle — Trade360 signals carry no
 * confidence field and no `match_operations` concept exists here; see the
 * old (LSports ENGAGE) `LiveMatchEventsTab`, which is untouched and unrelated.
 */
export function Trade360LiveEventsTab() {
  const { data: confirmedRefs = [], isLoading, isError } = useConfirmedMatchExternalRefs();
  const [fixtureIdInput, setFixtureIdInput] = useState('');
  const [watchedIds, setWatchedIds] = useState<string[]>([]);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<Trade360SignalType[]>([...SIGNAL_TYPES]);
  const cardRefs = useRef(new Map<string, HTMLDivElement | null>());

  const boundExternalIds = useMemo(
    () => new Set(confirmedRefs.map(r => r.externalId)),
    [confirmedRefs]
  );

  // A watched fixture that gets bound elsewhere while this tab is open (e.g.
  // confirmed from a group dialog) graduates into the auto-surfaced list —
  // drop it here so it isn't rendered twice.
  const unboundWatchedIds = useMemo(
    () => watchedIds.filter(id => !boundExternalIds.has(id)),
    [watchedIds, boundExternalIds]
  );

  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), 2_500);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const registerCard = useCallback((externalId: string, el: HTMLDivElement | null) => {
    cardRefs.current.set(externalId, el);
  }, []);

  const addFixture = useCallback(() => {
    const id = fixtureIdInput.trim();
    if (!id) return;
    if (boundExternalIds.has(id)) {
      setHighlightId(id);
      cardRefs.current.get(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    } else {
      setWatchedIds(prev => (prev.includes(id) ? prev : [...prev, id]));
    }
    setFixtureIdInput('');
  }, [fixtureIdInput, boundExternalIds]);

  const removeWatched = useCallback((id: string) => {
    setWatchedIds(prev => prev.filter(x => x !== id));
  }, []);

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ color: 'common.white' }}>
          Trade360 live events
        </Typography>
        <Chip size="small" variant="outlined" label={`${confirmedRefs.length} bound`} />
        {unboundWatchedIds.length > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={`${unboundWatchedIds.length} watching (unbound)`}
          />
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'center' }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96 }}>
              Add match
            </Typography>
            <TextField
              size="small"
              label="Fixture ID"
              value={fixtureIdInput}
              onChange={e => setFixtureIdInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') addFixture();
              }}
              sx={{ width: 200 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={addFixture}
              disabled={!fixtureIdInput.trim()}
            >
              Add
            </Button>
            <Typography variant="caption" color="text.secondary">
              Already bound to a group ⇒ jumps to it below. Otherwise watched here with a
              periodic snapshot only.
            </Typography>
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Signal types
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={selectedTypes}
              onChange={(_, v: Trade360SignalType[]) => setSelectedTypes(v)}
              aria-label="signal type filter"
            >
              {SIGNAL_TYPES.map(t => {
                const d = SIGNAL_TYPE_DISPLAY[t];
                return (
                  <ToggleButton key={t} value={t} aria-label={d.label}>
                    {d.emoji} {d.label}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Failed to load Trade360-bound matches.
          </Alert>
        )}
        {isLoading && confirmedRefs.length === 0 && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {!isLoading && confirmedRefs.length === 0 && unboundWatchedIds.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No Trade360-bound matches yet — bind one from a group's edit dialog, or watch an
            unbound fixture by adding its ID above.
          </Typography>
        )}
        {confirmedRefs.map(ref => (
          <BoundMatchCard
            key={`${ref.provider}:${ref.externalId}`}
            matchRef={ref}
            selectedTypes={selectedTypes}
            registerCard={registerCard}
            highlighted={highlightId === ref.externalId}
          />
        ))}
        {unboundWatchedIds.map(id => (
          <UnboundMatchCard key={id} fixtureId={id} onRemove={removeWatched} />
        ))}
      </Box>
    </Box>
  );
}

export default Trade360LiveEventsTab;
